import { useEffect, useLayoutEffect, useRef } from 'react';
import styled, { useTheme } from 'styled-components';
import dynamic from 'next/dynamic';
import { DFPSlotsProvider, DFPManager } from '@dsch/react-dfp';
import { useInView } from 'react-intersection-observer';
import { ShieldCheckmark2Ic } from '@dsch/dd-icons';
import {
  formatNumber,
  formatAddress,
  formatSellerType,
} from '../../../helpers/formatting';
import { useAdDetailsContext } from '../../../contexts/AdDetailsContext';
import { MiniUserProfile } from '../../Toolkit/MiniUserProfile/MiniUserProfile';
import { HtmlInfoPanel } from '../../Toolkit/HtmlInfoPanel/HtmlInfoPanel';
import { LegalInfoPanel } from '../../Toolkit/LegalInfoPanel/LegalInfoPanel';
import { AdTitleBox } from './components/AdTitleBox/AdTitleBox';
import { SellerInfoPanel } from './components/SellerInfoPanel/SellerInfoPanel';
import { SideSellerInfoPanel } from './components/SideSellerInfoPanel/SideSellerInfoPanel';
import { GreenlightPanel } from './components/GreenlightPanel/GreenlightPanel';
import { TrustedDealerPanel } from './components/TrustedDealerPanel/TrustedDealerPanel';
import { InfoPanel } from './components/InfoPanel/InfoPanel';
import { RevealPhoneNumber } from '../SharedComponents/RevealPhoneNumber/RevealPhoneNumber';
import { ShowAndToggleSaveAd } from '../SharedComponents/ShowAndToggleSaveAd/ShowAndToggleSaveAd';
import { RevealReportAd } from './components/RevealReportAd/RevealReportAd';
import { Breadcrumbs } from '../../ToolkitV2/Breadcrumbs/Breadcrumbs';
import { DealerPanel } from '../../Toolkit/DealerPanel/DealerPanel';
import { DealerSubtitle } from './components/DealerSubtitle/DealerSubtitle';
import type { Breadcrumb } from '../../ToolkitV2/Breadcrumbs/Breadcrumbs.typed';
import {
  VideoConfig,
  PhotoView,
  AdSummary,
  ISeoLinks,
  DataTargetingProps,
  PromoSlot,
  IFinanceBanner,
  Currency,
  IAdAttributeView,
} from '../../../types';
import { RevealSendMessage } from '../SharedComponents/RevealSendMessage/RevealSendMessage';
import { RevealDealerEnquiryModal } from '../SharedComponents/RevealDealerEnquiryModal/RevealDealerEnquiryModal';
import {
  DFPUnit,
  refreshAdUnits,
  getTargetingArguments,
  getDFPLazyLoadConfigAdDetailsPage,
} from '../../Toolkit/DFP/DFP';
import { useUserContext } from '../../../contexts/UserContext';
import { ButtonLink } from '../../Toolkit/Button/ButtonLink';
import { ShareAd } from '../../Toolkit/ShareAd/ShareAd';
import { Button, GhostButton } from '../../Toolkit/Button';
import { InfoTitle, IconContainer } from '../../Toolkit/InfoTitle/InfoTitle';
import { AD_DETAILS_DFP_CONFIG } from '../../../configs/dfpConfig';
import { AD_DETAILS_AD_SENSE_CONFIG } from '../../../configs/adSenseConfig';
import { useOnUpdateOnly } from '../../../hooks/UseOnUpdateOnly';
import { InternalLinks } from '../../Toolkit/InternalLinks/InternalLinks';
import { PromoPanelCollection } from './components/PromoPanelCollection/PromoPanelCollection';
import { FeaturedDealer } from '../../Toolkit/FeaturedDealer/FeaturedDealer';
import { SpecialInfoPanel } from '../../Toolkit/SpecialInfoPanel/SpecialInfoPanel';
import { UserActionsStickyFooter } from './components/UserActionsStickyFooter/UserActionsStickyFooter';

import type { AdDetailsComponentsThatAreNotInitiallyVisibleProps } from './components/Layouts/AdDetailsComponentsThatAreNotInitiallyVisible';
import type { RevealGalleryProps } from './components/RevealGallery/RevealGallery';
import type { GalleryPlaceholderProps } from './components/GalleryPlaceholder/GalleryPlaceholder';

import {
  SectionView,
  ServicePartner,
  DealerSummary,
  CardPayment,
  FinanceInfo,
  FinanceLink,
  GreenlightView,
  UserSummary,
  ApprovedProgrammeView,
  FeedbackTrackingSourcesNames,
  FEEDBACK_TRACKING_SOURCES,
  SELLER_TYPES,
  Reservation,
  FeedbackTrackingEventNames,
  FEEDBACK_TRACKING_EVENTS,
} from './types';
import { RelatedAds } from './components/RelatedAds/RelatedAds';
import { Link } from '../../Toolkit/Button/Link';
import { SimpleErrorBoundary } from '../../../helpers/SimpleErrorBoundary';
import { AdminPanel } from './components/AdminPanel/AdminPanel';
import { pushToDataLayer } from '../../../services/gtmService';
import { InfoTitleStarRating } from './components/GoogleReviews/InfoTitleStarRating/InfoTitleStarRating';
import { ElectricCarsHubPanel } from './components/ElectricCarsHubPanel/ElectricCarsHubPanel';

import { generateSaveSearchModalData, getSellerPhoto } from './helpers';
import { useAdSense } from '../../../hooks/UseAdSense';

import { adSenseAdDetailsStyleId } from '../../../helpers/constants';

import { AdSenseUnit } from '../../Toolkit/AdSenseUnit/AdSenseUnit';

import { Notification } from '../../Toolkit/Notification/Notification';
import { formatAuthorizationHeader } from '../../../helpers/auth';
import { useAuthOptions } from '../../../hooks/useAuthOptions';

import type { VehicleFeatures } from './components/VehicleFeatures/VehicleFeatures.typed';
import { mapVehicleFeatures } from './components/VehicleFeatures/helpers';
import { PAGE } from '../../../helpers/pages';
import { renderTrustedDealerInSection } from '../../../helpers/trustedDealerProgram';
import { useQueryParams } from '../../../hooks/UseQueryParams';
import { useWindowSize } from '../../../hooks/UseWindowSize';
import { EmailDealerOrSeller } from './components/EmailDealerOrSeller/EmailDealerOrSeller';
import { productRepository } from '../../../repositories/ProductsRepository';
import { isOk } from '../../../domains/Result';
import { fireToast } from '../../../helpers/Toasts';
import { useRevealDealerEnquiryModal } from '../SharedComponents/RevealDealerEnquiryModal/RevealDealerEnquiryModal.hook';
import { CarSummary } from './components/CarSummary/CarSummary';
import { WhitePanel } from '../../../components/Toolkit/WhitePanel/WhitePanel';
import type { VehicleSummary } from './components/CarSummary/CarSummary.typed';
import { useRouter } from 'next/router';
import { getCurrencyBasedOnCounty } from '../../../helpers/currency';

import { MileageIndicator } from '../../../api/types/CarValuationApiTypes';
import { VIEW_TYPE } from '../../ToolkitV2/CardV2/Card.typed';
import type { StructuredAttributes } from './DetailedVehicleInfo/DetailedVehicleInfo.typed';
import { SavedSearchPanel } from './components/SavedSearchPanel/SavedSearchPanel';
import getConfig from 'next/config';
import { MetaListItem } from '../../Toolkit/InfoTitle/MetaListItem/MetaListItem';
import { SellerRating } from './components/SellerRating/SellerRating';
const {
  publicRuntimeConfig: { FEATURE_FEEDBACK, FEATURE_FEEDBACK_ADP_BOTTOM },
} = getConfig();
import { VerifiedTitle } from './components/Verifications/VerifiedTitle/VerifiedTitle';
import { SellerInfoPanelV2 } from './components/SellerInfoPanelV2/SellerInfoPanelV2';

import {
  CDN_STATIC_ASSETS,
  HUBVISOR_SCRIPT_ENABLED,
  FEATURE_ADP_STICKY_CTA_ALWAYS_VISIBLE,
  FEATURE_TDP_UPDATES,
  FEATURE_FREE_HISTORY_CHECK,
} from '../../../utils';

import { scrollElementIntoView } from '../../../helpers/scroll';

import * as Styled from '../../../components/PageWrappers/AdDetails/AdDetails.styled';

import { useReservationTracking } from './hooks/ReservationTracking/ReservationTracking.hook';
import { logger } from '@dist-marketplace-frontend/services';
import { donedealBreakpoints } from 'styled-system';
import { useFreeHistoryCheckModal } from './components/FreeHistoryCheckModal/FreeHistoryCheckModal.hook';
const { media, SCREEN_XLARGE } = donedealBreakpoints;

export interface AdDetailsProps {
  displayExpiredForSeo?: boolean;
  state?: string;
  dfpNetworkId: string;
  environmentName: string;
  baseUrl: string;
  seller?: UserSummary;
  dealer?: DealerSummary;
  buyerTips?: {
    title: string;
    values: string[];
  };
  traderName?: string;
  publisherName?: string;
  header: string;
  description: string;
  currency: Currency;
  price: string | null;
  oldPriceView?: {
    oldPrice?: string | null;
    showOldPrice: boolean;
  };
  age?: string;
  views?: number;
  county?: string;
  id: number;
  emailResponse: boolean;
  phoneResponse: boolean;
  userSaved?: boolean;
  photos?: PhotoView[];
  link360?: string;
  link360Interior?: string;
  videoConfig?: VideoConfig;
  mediaCount?: number;
  section?: SectionView;
  breadcrumbs?: SectionView[];
  friendlyUrl?: string;
  depositEnabled: boolean;
  deliveryAvailable?: boolean;
  delivery?: {
    fee?: number;
    info?: string;
  };
  quantity?: number;
  buyNowSuccess?: boolean;
  boostInfoList?: {
    type: string;
    header: string;
    lines: string[];
    price: string;
  }[];
  youTubeVideoId?: string;
  youTubeReviewVideoId?: string;
  dataTargetingProps: DataTargetingProps;
  seoData?: ISeoLinks;
  displayAttributes?: IAdAttributeView[];
  servicePartners?: ServicePartner[];
  relatedAds?: AdSummary[];
  promoSlot?: PromoSlot;
  cardPayments?: CardPayment[];
  manufacturerApprovedBadge?: { manufacturer: string };
  meritsV2?: { field: string; text: string }[];
  herdwatchVerified?: boolean;
  specialOffer?: string;
  financeInfo: FinanceInfo | null;
  greenlightVerified?: boolean;
  greenlight?: GreenlightView;
  showHistorycheckV2?: boolean;
  campaignId?: string | null;
  financeLink?: FinanceLink;
  approvedProgramme?: ApprovedProgrammeView;
  countyTown?: string;
  wanted?: boolean;
  spotlight?: boolean;
  isAdmin?: boolean;
  finance?: {
    bannerUrl: string;
    banner?: IFinanceBanner;
  };
  buttonDisplayText?: string;
  isPreview?: boolean;
  adSensePublicKey: string;
  adAnalytics?: boolean;
  priceOnRequest?: boolean;
  vehicleFeatures?: VehicleFeatures;
  whatsappResponse: boolean;
  vehicleSummary?: VehicleSummary;
  mileageIndicator: MileageIndicator | null;
  relativeUrl: string;
  structuredAttributes?: StructuredAttributes;
  reservation?: Reservation;
}

interface ISMiniUserProfile {
  isDealer: boolean;
}

const AdDetailsComponentsThatAreNotInitiallyVisible =
  dynamic<AdDetailsComponentsThatAreNotInitiallyVisibleProps>(
    () =>
      import(
        '../../../components/PageWrappers/AdDetails/components/Layouts/AdDetailsComponentsThatAreNotInitiallyVisible'
      ).then((mod) => mod.AdDetailsComponentsThatAreNotInitiallyVisible),
    { ssr: false },
  );

const RevealGallery = dynamic<RevealGalleryProps>(
  import('./components/RevealGallery/RevealGallery').then(
    (mod) => mod.RevealGallery,
  ),
);

const GalleryPlaceholder = dynamic<GalleryPlaceholderProps>(
  import('./components/GalleryPlaceholder/GalleryPlaceholder').then(
    (mod) => mod.GalleryPlaceholder,
  ),
);

export const SMiniUserProfile = styled(MiniUserProfile)<ISMiniUserProfile>`
  background-color: ${({ theme }) => theme.colors.WHITE};
  padding: ${({ theme }) => `${theme.spacing.S12} ${theme.spacing.M16}`};
  border-radius: ${({ theme }) => `${theme.borderRadius.default}
    ${theme.borderRadius.default} 0 0`};
  ${media.large} {
    ${({ isDealer }) => !isDealer && 'display: none;'}
    padding: ${({ theme }) => `${theme.spacing.S12} ${theme.spacing.M24}`};
  }
  display: flex;
  position: relative;

  ${IconContainer} {
    margin-right: ${({ theme }) => theme.spacing.S8};
  }

  /* Allow this list item to collapse to support truncation within flex children */
  ${MetaListItem} {
    min-width: 0;
  }
`;

export const MiniUserProfileAndGalleryContainer = styled.div`
  border-radius: ${({ theme }) => theme.borderRadius.default};
`;

const GalleryContainer = styled.div`
  height: 226px;

  @media screen and (min-width: 374px) {
    height: 246px;
  }

  @media screen and (min-width: 413px) {
    height: 300px;
  }

  ${media.large} {
    height: 432px;
  }
`;

export const DetailAndGalleryContainer = styled.div`
  ${({ theme }) =>
    `
  border-radius: ${theme.borderRadius.default};
  background: ${theme.colors.WHITE};
  ${MiniUserProfileAndGalleryContainer} {
  }
  .ad-title-box {
    background: transparent;
  }

  ${GalleryContainer} {
    margin-bottom: 0px;
    height: auto;
    @media screen and (min-width: 374px) {
      height: auto;
    }

    @media screen and (min-width: 413px) {
      height: auto;
    }

    ${media.large} {
      height: auto;
    }
  }
  
  ${SGalleryPlaceholder}{
     height: 226px;

  @media screen and (min-width: 374px) {
    height: 246px;
  }

  @media screen and (min-width: 413px) {
    height: 300px;
  }

  ${media.large} {
    height: 432px;
  }
  `}
`;

const SHtmlInfoPanel = styled(HtmlInfoPanel)`
  display: none;

  ${media.large} {
    display: initial;
  }
`;

const SLegalInfoPanel = styled(LegalInfoPanel)`
  /* We give legal a little extra room with some padding */
  padding-top: ${({ theme }) => theme.spacing.M16};
  margin: ${({ theme }) => `0 ${theme.spacing.S8} ${theme.spacing.M24}`};

  ${media.large} {
    padding-top: ${({ theme }) => theme.spacing.M16};
    margin: ${({ theme }) => `0 ${theme.spacing.S8} ${theme.spacing.M32}`};
  }
`;

const SideColumn = styled.div`
  display: none;

  ${media.large} {
    display: block;
    padding-bottom: 24px;
  }
`;

const SSideSellerInfoPanel = styled(SideSellerInfoPanel)`
  display: none;

  ${media.large} {
    display: block;
  }
`;

const AdDetailsWrapper = styled.main`
  .mid-placement-extra-space {
    padding: ${({ theme }) => theme.spacing.S8} 0;
    ${media.large} {
      padding: 0;
    }
  }

  .fullscreen-placement-overlay {
    position: fixed;
    top: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 9000;
  }
`;

const RelatedAdsContainer = styled.div<{ keepPaddingOnDesktop: boolean }>`
  padding: 0 ${({ theme }) => theme.spacing.S8};

  ${media.large} {
    padding: 0
      ${({ theme, keepPaddingOnDesktop }) =>
        keepPaddingOnDesktop ? theme.spacing.S8 : 0};
  }
`;

const OffWhiteWrapper = styled.div<{ showBackground: boolean }>`
  border-radius: ${({ theme }) => theme.borderRadius.default};
  background-color: ${({ theme, showBackground }) =>
    showBackground ? theme.colors.OFFWHITE : 'none'};
`;

const OurStockHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.S8};
  display: flex;
  justify-content: space-between;
`;

const SNotification = styled(Notification)`
  ${({ theme }) => theme.fontSize.S12};
  line-height: ${({ theme }) => theme.spacing.M16};
  margin-right: ${({ theme }) => theme.spacing.S8};
  ${media.large} {
    ${({ theme }) => theme.fontSize.M16};
    svg {
      margin-right: ${({ theme }) => theme.spacing.S8};
    }
    padding: ${({ theme }) => theme.spacing.M16};
  }
`;

const OurStockHeading = styled.div`
  ${({ theme }) => theme.fontSize.L18};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

const ViewAllStock = styled.div`
  padding: ${({ theme }) => theme.spacing.M16} 0;
`;

const SInternalLinks = styled(InternalLinks)`
  background-color: ${({ theme }) => theme.colors.WHITE};
  padding: ${({ theme }) => theme.spacing.M16};

  ${media.medium} {
    padding: ${({ theme }) => theme.spacing.M24} 0;
  }
`;

const SeoContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.WHITE};
`;

const Sticky = styled.div`
  @media (min-height: 900px) {
    position: sticky;
    top: 0;
  }
`;

const TrustedDealerButton = styled(GhostButton)`
  flex: 0 0 auto;
`;

const TrustedDealerText = styled.span`
  display: none;
  ${({ theme }) => theme.fontSize.M16};
  color: ${({ theme }) => theme.colors.GREY_DARK};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  ${media.medium} {
    display: block;
  }
`;

export const SGalleryPlaceholder = styled(GalleryPlaceholder)<{
  isDealer: boolean;
}>`
  border-radius: ${({ theme }) =>
    `0 0 ${theme.borderRadius.default} ${theme.borderRadius.default}`};

  ${media.large} {
    border-radius: ${({ isDealer, theme }) =>
      isDealer
        ? `0 0 ${theme.borderRadius.default} ${theme.borderRadius.default}`
        : theme.borderRadius.default};
  }
`;

const SShieldCheckmark2Ic = styled(ShieldCheckmark2Ic)`
  ${media.smallOnly} {
    width: 32px;
    height: 32px;
  }
`;

function AdDetails({
  id: adId,
  displayExpiredForSeo = false,
  baseUrl,
  seller,
  dealer,
  buyerTips,
  header,
  description,
  currency,
  price,
  oldPriceView,
  age,
  views,
  county,
  id,
  phoneResponse,
  emailResponse,
  userSaved,
  breadcrumbs,
  section,
  photos,
  link360,
  link360Interior,
  videoConfig,
  mediaCount = 0,
  depositEnabled,
  deliveryAvailable,
  delivery,
  dfpNetworkId,
  youTubeVideoId,
  youTubeReviewVideoId,
  friendlyUrl,
  quantity,
  buyNowSuccess,
  dataTargetingProps,
  displayAttributes,
  servicePartners,
  relatedAds,
  promoSlot,
  seoData,
  manufacturerApprovedBadge,
  specialOffer,
  meritsV2,
  cardPayments,
  financeInfo,
  greenlightVerified,
  financeLink,
  greenlight,
  showHistorycheckV2,
  campaignId,
  approvedProgramme,
  countyTown,
  wanted,
  publisherName,
  isAdmin,
  finance,
  buttonDisplayText,
  isPreview,
  adSensePublicKey,
  adAnalytics,
  priceOnRequest = false,
  vehicleFeatures,
  state,
  whatsappResponse,
  vehicleSummary,
  mileageIndicator,
  relativeUrl,
  structuredAttributes,
  reservation,
}: AdDetailsProps) {
  const isSold = state === 'Sold';
  const theme = useTheme();
  const { accessToken, user } = useAuthOptions();
  const { checkIfQueryIsPresent } = useQueryParams();
  const isGalleryOpen = checkIfQueryIsPresent('modal', 'gallery');
  /**
   * Why this is here:
   *
   * Outlined in the {@link https://github.com/distilledmedia/dsch-donedeal-frontend/pull/2183#issue-2144705895|PR description}:
   */
  useEffect(() => {
    const timeout = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const {
    isAdSaved,
    setIsAdSaved,
    setIsShareModalOpen,
    isYoutubeReviewOpen,
    setIsYoutubeReviewOpen,
    setTrustedDealerModalOpen,
    setDealerBusinessTypeModalName,
    displayDfpUnits,
  } = useAdDetailsContext();

  const {
    setIsBoostModalOpen,
    setBoostModalData,
    consentOptions: { allowGoogle },
  } = useUserContext();

  useAdSense({
    options: {
      pubId: adSensePublicKey,
      styleId: adSenseAdDetailsStyleId,
      query: header,
      location: false,
      siteLinks: false,
    },
    dependencies: [allowGoogle],
    units: [AD_DETAILS_AD_SENSE_CONFIG.middle],
  });

  useOnUpdateOnly(() => {
    try {
      DFPManager.setTargetingArguments({
        ...dataTargetingProps.dfp,
        ...getTargetingArguments(),
      });

      DFPManager.reload(
        AD_DETAILS_DFP_CONFIG.dd_detail_top.slotId,
        AD_DETAILS_DFP_CONFIG.dd_detail_mid.slotId,
        AD_DETAILS_DFP_CONFIG.dd_detail_side.slotId,
      );
    } catch (e) {
      logger.error({
        message: 'Error setting targeting arguments on DFP',
        error: e,
        customData: {
          pageName: PAGE.AD_DETAILS,
          functionName: 'useOnUpdateOnly',
          dataTargetingProps,
          targetingArguments: getTargetingArguments(),
        },
      });
    }
  }, [id]);

  const handleHistoryChangePushToDataLayer =
    typeof window !== 'undefined' ? useLayoutEffect : useEffect;

  handleHistoryChangePushToDataLayer(() => {
    try {
      dataTargetingProps.gtm && pushToDataLayer(dataTargetingProps.gtm);
    } catch (e) {
      logger.error({
        message: 'Error pushing to data layer',
        error: e,
        customData: {
          dataTargetingProps,
          functionName: 'handleHistoryChangePushToDataLayer',
        },
      });
    }
  }, [dataTargetingProps.gtm]);

  handleHistoryChangePushToDataLayer(() => {
    try {
      displayExpiredForSeo && pushToDataLayer({ event: 'expired_ad_view' });
    } catch (e) {
      logger.error({
        message: 'Error pushing to data layer',
        error: e,
        customData: {
          event: 'expired_ad_view',
          functionName: 'handleHistoryChangePushToDataLayer',
        },
      });
    }
  }, []);

  useEffect(() => {
    try {
      window.addEventListener('message', messageReceivedFromDfpUnit);
      DFPManager.setTargetingArguments({
        ...dataTargetingProps.dfp,
        ...getTargetingArguments(),
      });

      return () => {
        window.removeEventListener('message', messageReceivedFromDfpUnit);
      };
    } catch (e) {
      logger.error({
        message: 'Error pushing to data layer',
        error: e,
        customData: {
          pageName: PAGE.AD_DETAILS,
          dataTargetingProps,
          targetingArguments: getTargetingArguments(),
          functionName: 'useEffect',
        },
      });
    }
    // TODO: Clean up this effect's dependencies. We're disabling this lint error for now so we can
    // clean up the lint logs. Ideally we should rewrite this code to be less error prone and trust
    // the lint rule's judgement.
    // https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=userstory/98606
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isGalleryOpen === false) {
      setIsYoutubeReviewOpen(false);
    }
    // TODO: Clean up this effect's dependencies. We're disabling this lint error for now so we can
    // clean up the lint logs. Ideally we should rewrite this code to be less error prone and trust
    // the lint rule's judgement.
    // https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=userstory/98606
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGalleryOpen]);

  useOnUpdateOnly(() => {
    setIsAdSaved && userSaved !== undefined && setIsAdSaved(userSaved);
  }, [id]);

  const { ref } = useInView({
    triggerOnce: true,
    rootMargin: '80px',
  });

  /*
   *  These stick CTA refs and in view booleans can be deleted when the FEATURE_ADP_STICKY_CTA_ALWAYS_VISIBLE flag is cleaned up and removed.
   *  The sticky CTAs will always be shown on mobile so no need to check if an element is in view or not.
   */

  const [mobileStickyCTARefOne, mobileStickyCTAinView2] = useInView({
    rootMargin: '20px',
  });

  const [mobileStickyCTARefTwo, mobileStickyCTAinView3] = useInView({
    rootMargin: '20px',
  });

  const [saveAdRef, saveAdInView] = useInView({
    initialInView: true,
  });

  const isViewingOwnAd = user?.id === seller?.id.toString();
  const editAdHref = `/update/${id}`;
  const showMiniShowroom = Boolean(dealer && dealer.showMiniShowroom);
  const isDealer = Boolean(dealer);
  const isTrader = Boolean(seller?.traderName);

  const sellerPhoto = getSellerPhoto(
    isDealer,
    FEATURE_FEEDBACK,
    dealer,
    seller,
  );

  const sellerTitle =
    ((dealer?.name || seller?.traderName) ?? publisherName ?? seller?.name) ||
    'Unknown';
  const sellerType = isDealer ? SELLER_TYPES.DEALER : seller?.type;
  const showGallery = Boolean(photos?.length || videoConfig);

  const savedSearchData = displayAttributes
    ? generateSaveSearchModalData(
        displayAttributes,
        price,
        currency,
        section,
        county,
        wanted,
      )
    : undefined;
  const eligibleForSaveSearch = section?.name === 'cars' && savedSearchData;
  const showSaveSearch = !isPreview && eligibleForSaveSearch;
  const showVehicleSummary = !!vehicleSummary;

  const showElectricCarHubPanel = displayAttributes?.some(
    (attribute: IAdAttributeView) => attribute.value === 'Electric',
  );

  function mapBreadcrumbs(breadcrumbs: SectionView[]): Array<Breadcrumb> {
    return breadcrumbs.map((breadcrumb: SectionView) => {
      return {
        href: `/${breadcrumb.name!}`,
        displayName: breadcrumb.displayName!,
      };
    });
  }

  function messageReceivedFromDfpUnit(event: MessageEvent) {
    const isTrusted = event.origin.includes('.googlesyndication.com');
    const frameEl = document.getElementById(
      'google_ads_iframe_5512429/donedeal/dd_detail_calculator_0',
    );
    const frameEl1 = document.getElementById(
      'google_ads_iframe_5512429/donedeal/dd_detail_textlink_0',
    );

    const { data } = event;
    const { calculatorTextLinkOpen } = data;

    if (frameEl && data.hasOwnProperty('dimensions') && isTrusted) {
      const { dimensions } = data;
      frameEl.style.height = dimensions.height === '100vh' ? '100vh' : 'auto';
      frameEl.style.width = '100%';
    }

    if (frameEl && data.hasOwnProperty('showModal') && isTrusted) {
      if (data.showModal === true) {
        frameEl.className = 'fullscreen-placement-overlay';
      } else {
        frameEl.className = '';
      }
    }

    if (frameEl1 && data.hasOwnProperty('dimensions') && isTrusted) {
      frameEl1.style.width = '100%';
    }

    if (frameEl1 && data.hasOwnProperty('showModal') && isTrusted) {
      if (data.showModal === true) {
        frameEl1.className = 'fullscreen-placement-overlay';
        if (frameEl && calculatorTextLinkOpen) {
          frameEl.style.width = '0';
          frameEl.style.height = '0';
          frameEl.style.minWidth = '0';
        } else {
          if (frameEl) {
            frameEl.style.width = '100%';
            frameEl.style.height = '100vh';
            frameEl.style.minWidth = '100%';
          }
        }
      } else {
        frameEl1.className = '';
      }
    }
  }

  /** For use with Gallery and Post Message Modals on close */
  function refreshTopAndSideDFPUnit() {
    const adUnitsToRefresh = [
      AD_DETAILS_DFP_CONFIG.dd_detail_top.slotId,
      AD_DETAILS_DFP_CONFIG.dd_detail_side.slotId,
    ];

    refreshAdUnits(adUnitsToRefresh);
  }

  const breadcrumbsList = breadcrumbs && mapBreadcrumbs(breadcrumbs);

  const breadcrumbSectionName =
    breadcrumbs && breadcrumbs.length > 1 ? breadcrumbs[1].name : null;

  const similarAds =
    breadcrumbsList && !isPreview
      ? breadcrumbsList[breadcrumbsList.length - 1].href
      : null;

  const isTrustedDealer =
    dealer?.trustedDealer && renderTrustedDealerInSection(section?.name || '');

  const displayTrustedDealerPanel = isTrustedDealer;

  const { width } = useWindowSize();
  const isDesktop = width && width >= SCREEN_XLARGE;

  const handleBoost = async () => {
    if (!section?.id) return;
    const data = await productRepository.getUpsells(
      id,
      county ? getCurrencyBasedOnCounty(county) : 'EUR',
      section?.id,
      photos?.length || 0,
      wanted || false,
      price || '0',
      formatAuthorizationHeader(accessToken),
    );
    if (isOk(data)) {
      const upsells = data.ok;
      setBoostModalData({
        ...upsells,
        section: section.displayName,
      });
      setIsBoostModalOpen(true);
    } else {
      fireToast({
        type: 'ERROR',
        text: 'Oops! Something went wrong, please try again later',
      });
    }
  };
  const { handleReveal } = useRevealDealerEnquiryModal({
    adId: id,
    accessToken,
  });

  const { back } = useRouter();

  const sellerTypeText = formatSellerType(
    isTrader,
    seller?.verification,
    seller?.type,
  );

  const renderVerifiedTitle = !isDealer && FEATURE_FEEDBACK;

  const displaySellerInfoPanel =
    !isPreview && seller && !dealer && !displayExpiredForSeo;

  const sellerInfoPanelRef = useRef<HTMLDivElement>(null);
  const ratingsPanelRef = useRef<HTMLDivElement>(null);
  const dealerInfoPanelRef = useRef<HTMLDivElement>(null);

  const trackScrollToFeedback = (
    event: FeedbackTrackingEventNames,
    source: FeedbackTrackingSourcesNames,
  ) => {
    pushToDataLayer({
      event,
      source,
    });
  };

  const scrollToSellerInfoAndTrack = (source: FeedbackTrackingSourcesNames) => {
    if (sellerInfoPanelRef?.current) {
      scrollElementIntoView({
        element: sellerInfoPanelRef.current,
        shouldFocus: true,
      });
    }
    trackScrollToFeedback(
      FEEDBACK_TRACKING_EVENTS.sellerFeedbackScrollClick,
      source,
    );
  };

  const scrollToRatingsAndTrack = (source: FeedbackTrackingSourcesNames) => {
    if (ratingsPanelRef?.current) {
      scrollElementIntoView({
        element: ratingsPanelRef.current,
        shouldFocus: true,
      });
    }
    trackScrollToFeedback(
      FEEDBACK_TRACKING_EVENTS.sellerFeedbackScrollClick,
      source,
    );
  };

  const scrollToDealerProfileAndTrack = (
    name: FeedbackTrackingEventNames,
    source: FeedbackTrackingSourcesNames,
  ) => {
    if (dealerInfoPanelRef?.current) {
      scrollElementIntoView({
        element: dealerInfoPanelRef.current,
        shouldFocus: true,
      });
    }
    trackScrollToFeedback(name, source);
  };

  const onClickTitle = (source: FeedbackTrackingSourcesNames) => {
    if (dealer) {
      scrollToDealerProfileAndTrack(
        FEEDBACK_TRACKING_EVENTS.sellerFeedbackScrollClick,
        source,
      );
    } else {
      scrollToSellerInfoAndTrack(source);
    }
  };

  useReservationTracking({ reservationStatus: reservation?.status, adId });

  const { open: openFreeHistoryCheckModal } = useFreeHistoryCheckModal();

  return (
    <AdDetailsWrapper>
      <DFPSlotsProvider
        dfpNetworkId={dfpNetworkId}
        {...(HUBVISOR_SCRIPT_ENABLED
          ? {}
          : getDFPLazyLoadConfigAdDetailsPage())}
      >
        {!isPreview && breadcrumbsList && (
          <div className="row common-margin-bottom">
            <div className="columns small-12">
              <Breadcrumbs breadcrumbs={breadcrumbsList} goBack={back} />
            </div>
          </div>
        )}

        {!isPreview && isAdmin && (
          <AdminPanel adId={id} userId={seller?.id} baseUrl={baseUrl} />
        )}

        {!isPreview && !displayExpiredForSeo && displayDfpUnits && (
          <DFPUnit
            className="common-margin-bottom"
            {...AD_DETAILS_DFP_CONFIG.dd_detail_top}
          />
        )}

        {!isPreview && displayExpiredForSeo && similarAds && (
          <div className={'row'}>
            <div className="columns medium-12 large-8 large-offset-2">
              <SNotification className="common-margin-bottom" intent={'ERROR'}>
                This item has been marked as sold, expired or removed by the
                seller. <a href={similarAds}>View similar ads here.</a>
              </SNotification>
            </div>
          </div>
        )}

        <div
          className={!isPreview ? 'row small-collapse medium-uncollapse' : ''}
        >
          <div className={!isPreview ? 'columns small-12 large-8' : ''}>
            <DetailAndGalleryContainer>
              <MiniUserProfileAndGalleryContainer>
                {(seller?.traderName ?? seller?.name) &&
                  !displayExpiredForSeo && (
                    <SMiniUserProfile
                      title={
                        renderVerifiedTitle && seller?.verification ? (
                          <VerifiedTitle
                            title={sellerTitle}
                            verifications={seller.verification}
                            onClickTitle={() => {
                              scrollToSellerInfoAndTrack(
                                FEEDBACK_TRACKING_SOURCES.topPanelTitle,
                              );
                            }}
                          />
                        ) : (
                          <Styled.TitleButton
                            ofType="SECONDARY"
                            fontSize="L18"
                            onClick={() =>
                              onClickTitle(
                                FEEDBACK_TRACKING_SOURCES.topPanelTitle,
                              )
                            }
                          >
                            {sellerTitle}
                          </Styled.TitleButton>
                        )
                      }
                      icon={
                        <Styled.UserProfileIconContainer
                          onClick={() =>
                            isDealer
                              ? scrollToDealerProfileAndTrack(
                                  FEEDBACK_TRACKING_EVENTS.sellerFeedbackScrollClick,
                                  FEEDBACK_TRACKING_SOURCES.topPanelIcon,
                                )
                              : scrollToSellerInfoAndTrack(
                                  FEEDBACK_TRACKING_SOURCES.topPanelIcon,
                                )
                          }
                        >
                          <img src={sellerPhoto} alt={sellerTitle} />
                        </Styled.UserProfileIconContainer>
                      }
                      county={county}
                      isTrader={isTrader}
                      type={seller.type}
                      franchiseType={dealer?.franchiseType}
                      verification={seller.verification}
                      isDealer={Boolean(dealer)}
                      onClickSubtitle={() =>
                        onClickTitle(FEEDBACK_TRACKING_SOURCES.topPanelSubtitle)
                      }
                      subtitleComponent={
                        dealer ? (
                          <InfoTitleStarRating
                            displayReviews={Boolean(
                              dealer?.googleReview?.displayReviews,
                            )}
                            rating={
                              dealer?.googleReview &&
                              dealer.googleReview?.rating
                            }
                            maxRating={dealer?.googleReview && 5}
                            isDealer={Boolean(dealer)}
                            franchiseType={dealer?.franchiseType}
                            isTrustedDealer={dealer?.trustedDealer}
                            county={county}
                            isTrader={isTrader}
                            verification={seller.verification}
                            type={seller.type}
                            ratingGroupDataTracking={
                              'google-reviews-rating-group'
                            }
                            variant="LARGE"
                            onClickSubtitle={() =>
                              isDealer
                                ? scrollToDealerProfileAndTrack(
                                    FEEDBACK_TRACKING_EVENTS.sellerFeedbackScrollClick,
                                    FEEDBACK_TRACKING_SOURCES.topPanelSubtitle,
                                  )
                                : scrollToSellerInfoAndTrack(
                                    FEEDBACK_TRACKING_SOURCES.topPanelSubtitle,
                                  )
                            }
                          />
                        ) : FEATURE_FEEDBACK && !isDealer ? (
                          <SellerRating
                            sellerTypeText={sellerTypeText}
                            rating={seller.rating}
                            onClickRatings={() =>
                              scrollToRatingsAndTrack(
                                FEEDBACK_TRACKING_SOURCES.mobileAvgRatingReviewScore,
                              )
                            }
                            onClickReviews={() =>
                              scrollToSellerInfoAndTrack(
                                FEEDBACK_TRACKING_SOURCES.topPanelSubtitle,
                              )
                            }
                          />
                        ) : null
                      }
                      renderAdditionalHeaderInfo={
                        isTrustedDealer &&
                        !FEATURE_TDP_UPDATES && (
                          <TrustedDealerButton
                            aria-label="Open Trusted Dealer Information Modal"
                            onClick={() => setTrustedDealerModalOpen(true)}
                            data-tracking="trusted-dealer-modal-button"
                          >
                            <SShieldCheckmark2Ic
                              width="24"
                              height="24"
                              color={theme.colors.GREEN}
                            />
                            <TrustedDealerText>
                              Trusted Dealer
                            </TrustedDealerText>
                          </TrustedDealerButton>
                        )
                      }
                    />
                  )}
                <GalleryContainer className="common-margin-bottom">
                  {showGallery ? (
                    <RevealGallery
                      cdnUrl={CDN_STATIC_ASSETS}
                      trackingId="ad-open-photo-gallery"
                      photos={photos}
                      photo={photos && photos[0]}
                      videoConfig={videoConfig}
                      link360={link360}
                      link360Interior={link360Interior}
                      mediaCount={mediaCount}
                      title={header}
                      price={price}
                      currency={currency}
                      oldPrice={
                        (oldPriceView?.showOldPrice && oldPriceView.oldPrice) ||
                        undefined
                      }
                      pricePerMonth={
                        financeInfo?.pricePerMonth.priceDisplay || undefined
                      }
                      renderShareAd={
                        <ShareAd
                          handleShareAd={() => setIsShareModalOpen(true)}
                          iconSizeDesktop="SM"
                        />
                      }
                      renderSaveAd={
                        userSaved !== undefined && (
                          <ShowAndToggleSaveAd
                            adId={id}
                            isUnderlined={false}
                            isAdSaved={isAdSaved}
                            setIsAdSaved={setIsAdSaved}
                            isViewingOwnAd={isViewingOwnAd}
                            subsection={section?.name}
                            sellerType={sellerType}
                          />
                        )
                      }
                      renderDfpUnit={
                        displayDfpUnits && (
                          <DFPUnit
                            className="common-margin-bottom"
                            {...AD_DETAILS_DFP_CONFIG.dd_detail_photobrowser}
                          />
                        )
                      }
                      keyInfo={[age, `${formatNumber(views)} views`, county]}
                      youTubeVideoId={youTubeVideoId}
                      youTubeReviewVideoId={youTubeReviewVideoId}
                      isDealer={isDealer}
                      isYoutubeReviewOpen={isYoutubeReviewOpen}
                      setIsYoutubeReviewOpen={setIsYoutubeReviewOpen}
                      isPreview={isPreview}
                      refreshTopAndSideDFPUnit={refreshTopAndSideDFPUnit}
                    />
                  ) : (
                    <SGalleryPlaceholder
                      isDealer={isDealer}
                      buttonLink={editAdHref}
                      isViewingOwnAd={isViewingOwnAd}
                      className="common-margin-bottom"
                    />
                  )}
                </GalleryContainer>
              </MiniUserProfileAndGalleryContainer>
              <AdTitleBox
                adId={id}
                wanted={wanted}
                title={header}
                price={price}
                currency={currency}
                financeInfo={financeInfo}
                financeLink={financeLink}
                oldPrice={
                  (oldPriceView?.showOldPrice && oldPriceView.oldPrice) ||
                  undefined
                }
                listItems={[
                  age,
                  `${formatNumber(views)} views`,
                  `${countyTown ? `${countyTown}, ` : ''}${county}`,
                ]}
                renderSaveAd={
                  (userSaved !== undefined || isPreview) && (
                    <div ref={saveAdRef}>
                      {saveAdInView && (
                        <ShowAndToggleSaveAd
                          adId={id}
                          isUnderlined={false}
                          userSaved={userSaved}
                          isAdSaved={isAdSaved}
                          setIsAdSaved={setIsAdSaved}
                          isViewingOwnAd={isViewingOwnAd}
                          isPreview={isPreview}
                          subsection={section?.name}
                          sellerType={sellerType}
                        />
                      )}
                    </div>
                  )
                }
                renderBoostButton={
                  isViewingOwnAd && <Button onClick={handleBoost}>Boost</Button>
                }
                renderEditButton={
                  <ButtonLink href={editAdHref} ofType="SECONDARY">
                    Edit
                  </ButtonLink>
                }
                isViewingOwnAd={isViewingOwnAd}
                className="common-margin-bottom ad-title-box"
                handleShareAd={() => setIsShareModalOpen(true)}
                deliveryAvailable={deliveryAvailable}
                deliveryFee={delivery?.fee}
                quantity={quantity}
                buyNowSuccess={buyNowSuccess}
                manufacturerApprovedBadge={manufacturerApprovedBadge}
                specialOffer={specialOffer}
                meritsV2={meritsV2}
                greenlightVerified={greenlightVerified}
                cardPayments={cardPayments}
                carFinance={finance}
                buttonDisplayText={buttonDisplayText}
                depositEnabled={depositEnabled}
                adAnalytics={adAnalytics}
                priceOnRequest={priceOnRequest}
                isSold={isSold}
              />
            </DetailAndGalleryContainer>
            {(showVehicleSummary || showSaveSearch) && (
              <WhitePanel className="common-margin-bottom">
                {showVehicleSummary && (
                  <CarSummary
                    vehicleSummary={vehicleSummary}
                    mileageIndicator={mileageIndicator}
                  />
                )}
                {showSaveSearch && <SavedSearchPanel adId={adId} />}
              </WhitePanel>
            )}
            {!FEATURE_TDP_UPDATES && !isPreview && greenlight && (
              <WhitePanel className="common-margin-bottom">
                <GreenlightPanel
                  listBlock={greenlight.listBlock}
                  titleGroup={greenlight.titleGroup}
                  infoLink={greenlight.infoLink}
                  greenlightVerified={greenlightVerified}
                  showHistoryCheck={showHistorycheckV2}
                  className="common-margin-bottom"
                  data-testid="greenlight-panel"
                />
              </WhitePanel>
            )}
            {FEATURE_TDP_UPDATES &&
              ((!isPreview && greenlight) || displayTrustedDealerPanel) && (
                <WhitePanel className="common-margin-bottom">
                  {displayTrustedDealerPanel && (
                    <TrustedDealerPanel
                      dealer={dealer}
                      onClickTDPSubtitle={() => setTrustedDealerModalOpen(true)}
                      onClickRatings={() =>
                        scrollToDealerProfileAndTrack(
                          FEEDBACK_TRACKING_EVENTS.adGoogleReviewsClick,
                          FEEDBACK_TRACKING_SOURCES.tdpPanel,
                        )
                      }
                      onClickBusinessType={() =>
                        // Here we set the 'name' for the business type modal. Setting the name applies the correct content to the modal and opens it.
                        setDealerBusinessTypeModalName(
                          dealer?.businessType ?? null,
                        )
                      }
                      dataTestid="trusted-dealer-panel"
                    />
                  )}
                  {!isPreview && greenlight && displayTrustedDealerPanel && (
                    <WhitePanel.Divider />
                  )}
                  {FEATURE_FREE_HISTORY_CHECK && (
                    <>
                      <WhitePanel.Divider />
                      <div>
                        <button onClick={openFreeHistoryCheckModal}>
                          Open FHC
                        </button>
                      </div>
                      <WhitePanel.Divider />
                    </>
                  )}
                  {!isPreview && greenlight && (
                    <GreenlightPanel
                      listBlock={greenlight.listBlock}
                      titleGroup={greenlight.titleGroup}
                      infoLink={greenlight.infoLink}
                      greenlightVerified={greenlightVerified}
                      showHistoryCheck={showHistorycheckV2}
                      dataTestid="greenlight-panel"
                    />
                  )}
                </WhitePanel>
              )}
            {showElectricCarHubPanel && (
              <ElectricCarsHubPanel className="common-margin-bottom" />
            )}
            <InfoPanel
              title="Description"
              information={description}
              renderAction={
                !isPreview && (
                  <RevealReportAd
                    isViewingOwnAd={isViewingOwnAd}
                    editAdHREF={editAdHref}
                  />
                )
              }
              className="common-margin-bottom"
              cdnUrl={CDN_STATIC_ASSETS}
              section={section}
              displayAttributes={displayAttributes}
              servicePartners={servicePartners}
              renderDfpUnit={
                !isPreview &&
                !displayExpiredForSeo &&
                displayDfpUnits && (
                  <DFPUnit {...AD_DETAILS_DFP_CONFIG.dd_detail_calculator} />
                )
              }
              isPreview={isPreview}
              vehicleFeatures={
                vehicleFeatures && mapVehicleFeatures(vehicleFeatures)
              }
              structuredAttributes={structuredAttributes}
              isDealer={isDealer}
              emailResponse={emailResponse}
            />
            {dataTargetingProps.dfp &&
              dataTargetingProps.dfp?.vertical &&
              dataTargetingProps.dfp?.vertical[0] === 'Marketplace' && (
                <div ref={ref} className="common-margin-bottom">
                  <AdSenseUnit unit={AD_DETAILS_AD_SENSE_CONFIG.middle} />
                </div>
              )}
            {!isPreview && displayDfpUnits && (
              <DFPUnit
                className="common-margin-bottom mid-placement-extra-space"
                {...AD_DETAILS_DFP_CONFIG.dd_detail_mid}
              />
            )}
            {!isPreview && !isDesktop && (
              <PromoPanelCollection
                breadcrumbSectionName={breadcrumbSectionName}
                sectionName={section?.name}
                cdnUrl={CDN_STATIC_ASSETS}
                showGreenlight={showHistorycheckV2}
                financeInfo={financeInfo}
                className="common-margin-bottom"
              />
            )}
            {approvedProgramme && (
              <SpecialInfoPanel
                id={approvedProgramme.id}
                hero={approvedProgramme.hero}
                benefits={approvedProgramme.benefits}
                moreInfo={approvedProgramme.moreInfo}
                description={approvedProgramme.description}
                terms={approvedProgramme.terms}
                className="common-margin-bottom"
              />
            )}
            <div
              ref={
                FEATURE_ADP_STICKY_CTA_ALWAYS_VISIBLE
                  ? null
                  : mobileStickyCTARefOne
              }
            >
              {!isPreview && (
                <UserActionsStickyFooter
                  adId={adId}
                  campaignId={campaignId}
                  showNumberTextVariant
                  sectionName={section?.name}
                  phoneResponse={phoneResponse}
                  emailResponse={emailResponse}
                  whatsappResponse={whatsappResponse}
                  handleReveal={handleReveal}
                  sellerType={sellerType}
                  editAdHref={editAdHref}
                  isViewingOwnAd={isViewingOwnAd}
                  handleBoost={handleBoost}
                  active={!mobileStickyCTAinView2 && !mobileStickyCTAinView3}
                  reservationStatus={reservation?.status}
                />
              )}
              <div ref={ref}>
                {displaySellerInfoPanel &&
                  (FEATURE_FEEDBACK_ADP_BOTTOM ? (
                    <SellerInfoPanelV2
                      ratingsPanelRef={ratingsPanelRef}
                      sellerInfoPanelRef={sellerInfoPanelRef}
                      onClickRatings={() =>
                        scrollToRatingsAndTrack(
                          FEEDBACK_TRACKING_SOURCES.ratingScoreBottom,
                        )
                      }
                      onClickReviews={() =>
                        scrollToRatingsAndTrack(
                          FEEDBACK_TRACKING_SOURCES.reviewCountBottom,
                        )
                      }
                      title={
                        seller?.verification ? (
                          <VerifiedTitle
                            title={sellerTitle}
                            verifications={seller.verification}
                          />
                        ) : (
                          sellerTitle
                        )
                      }
                      profileImageUrl={sellerPhoto}
                      renderSendMessage={
                        emailResponse &&
                        !isViewingOwnAd && (
                          <RevealSendMessage
                            testId="bottom-panel-send-message"
                            trackingId="contact-seller-send-btn-large-bottom"
                            clickSource="bottom-panel"
                          />
                        )
                      }
                      renderShowPhone={
                        phoneResponse &&
                        !isViewingOwnAd && (
                          <RevealPhoneNumber
                            adId={adId}
                            clickSource="bottom-panel"
                            isPrimaryButton={!emailResponse}
                            campaignId={campaignId}
                            sectionName={section?.name}
                            sellerType={sellerType}
                          />
                        )
                      }
                      county={seller.county}
                      type={seller.type}
                      sellerUrl={`${baseUrl}/all?userId=${seller.id}`}
                      isTrader={isTrader}
                      verification={seller.verification}
                      registrationDate={seller.registrationDate}
                      adCountStats={seller.adCountStats}
                      avgResponseRate={seller.avgResponseRate}
                      testId="bottom-seller-panel"
                      className="common-margin-bottom"
                      userRating={seller?.rating}
                      membershipDuration={seller?.membershipDuration}
                    />
                  ) : (
                    <SellerInfoPanel
                      title={sellerTitle}
                      profileImageUrl={sellerPhoto}
                      renderSendMessage={
                        emailResponse &&
                        !isViewingOwnAd && (
                          <RevealSendMessage
                            testId="bottom-panel-send-message"
                            trackingId="contact-seller-send-btn-large-bottom"
                            clickSource="bottom-panel"
                          />
                        )
                      }
                      renderShowPhone={
                        phoneResponse &&
                        !isViewingOwnAd && (
                          <RevealPhoneNumber
                            adId={adId}
                            clickSource="bottom-panel"
                            isPrimaryButton={!emailResponse}
                            campaignId={campaignId}
                            sectionName={section?.name}
                            sellerType={sellerType}
                          />
                        )
                      }
                      county={seller.county}
                      type={seller.type}
                      sellerUrl={`${baseUrl}/all?userId=${seller.id}`}
                      isTrader={isTrader}
                      verification={seller.verification}
                      registrationDate={seller.registrationDate}
                      adCountStats={seller.adCountStats}
                      avgResponseRate={seller.avgResponseRate}
                      testId="bottom-seller-panel"
                      className="common-margin-bottom"
                    />
                  ))}
                <OffWhiteWrapper
                  showBackground={showMiniShowroom}
                  ref={dealerInfoPanelRef}
                >
                  {!isPreview && dealer && (
                    <SimpleErrorBoundary>
                      <DealerPanel
                        title={dealer.name}
                        smallLogo={dealer?.logo?.small}
                        listItems={
                          <DealerSubtitle
                            isTrustedDealer={isTrustedDealer}
                            franchiseType={dealer.franchiseType}
                            establishedYear={dealer.establishedYear?.toString()}
                          />
                        }
                        affiliateLogo={
                          dealer.type === 'SIMI'
                            ? `${CDN_STATIC_ASSETS}/images/illustrations/SIMI.svg`
                            : undefined
                        }
                        showRoomImage={dealer?.forecourtPhoto?.medium}
                        largeLogo={dealer?.logo?.medium}
                        renderMakeEnquiry={
                          emailResponse && (
                            <RevealDealerEnquiryModal
                              adId={id}
                              id="dealer-enquiry-btn-dealer-panel"
                              handleReveal={handleReveal}
                            />
                          )
                        }
                        renderShowPhone={
                          phoneResponse && (
                            <RevealPhoneNumber
                              adId={adId}
                              clickSource="dealer-panel"
                              isPrimaryButton={!emailResponse}
                              campaignId={campaignId}
                              sectionName={section?.name}
                              sellerType={sellerType}
                            />
                          )
                        }
                        showroomUrl={dealer.showroomUrl}
                        location={formatAddress(
                          dealer.address,
                          dealer.enhancedAddress,
                          true,
                        )}
                        address={formatAddress(
                          dealer.address,
                          dealer.enhancedAddress,
                        )}
                        latitude={dealer?.latitude}
                        longitude={dealer?.longitude}
                        openingHours={dealer.enhancedOpeningHours?.days}
                        websiteUrl={dealer.websiteURL}
                        googleReviews={dealer?.googleReview}
                        adId={adId}
                      />
                    </SimpleErrorBoundary>
                  )}
                  {!isPreview && relatedAds?.length && (
                    <RelatedAdsContainer
                      className="common-margin-bottom"
                      keepPaddingOnDesktop={showMiniShowroom}
                    >
                      {showMiniShowroom ? (
                        <OurStockHeader>
                          <OurStockHeading>Our Stock</OurStockHeading>
                          <Link
                            ofType="SECONDARY"
                            href={dealer!.showroomUrl}
                            underline
                          >
                            Total Stock: {dealer!.totalAds} Ad
                            {dealer!.totalAds === 1 ? '' : 's'}
                          </Link>
                        </OurStockHeader>
                      ) : (
                        <InfoTitle
                          title="You may also like"
                          listItems={['Similar items in your price range']}
                          className="common-margin-bottom"
                        />
                      )}
                      <RelatedAds
                        relatedAds={relatedAds}
                        viewType={
                          showMiniShowroom ? VIEW_TYPE.LIST : VIEW_TYPE.GRID
                        }
                        testId="related-ads"
                        domain={baseUrl}
                      />

                      {!isPreview && showMiniShowroom && (
                        <ViewAllStock>
                          <Link
                            ofType="SECONDARY"
                            href={dealer!.showroomUrl}
                            underline
                          >
                            View All Stock
                          </Link>
                        </ViewAllStock>
                      )}
                    </RelatedAdsContainer>
                  )}
                </OffWhiteWrapper>
                <SLegalInfoPanel />
              </div>
            </div>
          </div>
          {!isPreview && (
            <SideColumn className="columns large-4">
              {(seller?.traderName ?? seller?.name) &&
                !displayExpiredForSeo && (
                  <SSideSellerInfoPanel
                    onClickRatings={() =>
                      scrollToSellerInfoAndTrack(
                        FEEDBACK_TRACKING_SOURCES.desktopAvgRatingScoreTop,
                      )
                    }
                    onClickDealerRatings={() =>
                      scrollToDealerProfileAndTrack(
                        FEEDBACK_TRACKING_EVENTS.adGoogleReviewsClick,
                        FEEDBACK_TRACKING_SOURCES.tdpSidePanel,
                      )
                    }
                    onClickReviews={() =>
                      scrollToSellerInfoAndTrack(
                        FEEDBACK_TRACKING_SOURCES.desktopReviewCountTop,
                      )
                    }
                    onClickDealerReviews={() =>
                      scrollToDealerProfileAndTrack(
                        FEEDBACK_TRACKING_EVENTS.sellerFeedbackScrollClick,
                        FEEDBACK_TRACKING_SOURCES.tdpSidePanel,
                      )
                    }
                    onClickTitle={() =>
                      onClickTitle(FEEDBACK_TRACKING_SOURCES.sidePanelTitle)
                    }
                    onClickTrustedDealerType={() =>
                      setTrustedDealerModalOpen(true)
                    }
                    renderSendMessage={
                      emailResponse && (
                        <EmailDealerOrSeller
                          adId={adId}
                          sellerType={sellerType}
                          handleReveal={handleReveal}
                          dealerID="dealer-enquiry-btn-side-panel"
                          clickSource="side-panel"
                        />
                      )
                    }
                    renderShowPhone={
                      (phoneResponse && (
                        <RevealPhoneNumber
                          adId={id}
                          isPrimaryButton={!emailResponse}
                          clickSource="side-panel"
                          campaignId={campaignId}
                          sectionName={section?.name}
                          sellerType={sellerType}
                        />
                      )) ||
                      undefined
                    }
                    title={sellerTitle}
                    isTrader={isTrader}
                    county={county}
                    type={seller.type}
                    verification={seller.verification}
                    sellerUrl={`${baseUrl}/all?userId=${seller.id}`}
                    testId="side-seller-panel"
                    className="common-margin-bottom"
                    isDealer={isDealer}
                    showroomUrl={dealer?.showroomUrl}
                    illustration={sellerPhoto}
                    isTrustedDealer={dealer?.trustedDealer}
                    franchiseType={dealer?.franchiseType}
                    dealerReviews={dealer?.googleReview?.userRatingsTotal}
                    dealerRating={dealer?.googleReview?.rating}
                    displayDealerReviews={Boolean(
                      dealer?.googleReview?.displayReviews,
                    )}
                    dealerRatingCategory={dealer?.googleReview?.ratingCategory}
                    userRating={seller?.rating}
                    membershipDuration={seller?.membershipDuration}
                    dealerMembershipDuration={dealer?.membershipDuration}
                    reservationStatus={reservation?.status}
                  />
                )}

              <Sticky>
                {displayDfpUnits && (
                  <DFPUnit
                    className="common-margin-bottom"
                    {...AD_DETAILS_DFP_CONFIG.dd_detail_side}
                  />
                )}

                {isDesktop && (
                  <PromoPanelCollection
                    breadcrumbSectionName={breadcrumbSectionName}
                    sectionName={section?.name}
                    cdnUrl={CDN_STATIC_ASSETS}
                    showGreenlight={showHistorycheckV2}
                    financeInfo={financeInfo}
                    className="common-margin-bottom"
                  />
                )}

                {buyerTips && (
                  <SHtmlInfoPanel
                    heading={buyerTips.title}
                    htmlArray={buyerTips.values}
                  />
                )}
              </Sticky>
            </SideColumn>
          )}
        </div>
        <div
          ref={
            FEATURE_ADP_STICKY_CTA_ALWAYS_VISIBLE ? null : mobileStickyCTARefTwo
          }
        >
          {!isPreview &&
            seoData?.internalLinks &&
            seoData?.internalLinks.length !== 0 && (
              <SeoContainer>
                <div className="row small-collapse medium-uncollapse">
                  <div className="columns small-12">
                    <SInternalLinks
                      title="You may also be interested in"
                      links={seoData.internalLinks}
                    />
                  </div>
                </div>
              </SeoContainer>
            )}

          {!isPreview && promoSlot && (
            <FeaturedDealer
              domain={baseUrl}
              cdnUrl={CDN_STATIC_ASSETS}
              dealerLogo={promoSlot.largeLogoUrl ?? promoSlot.logoUrl}
              title={promoSlot.display}
              listItems={[
                promoSlot.dealerType,
                `Total Stock: ${promoSlot.totalAdCount} Ads`,
              ]}
              showRoomUrl={promoSlot.showroomUrl}
              showRoomText="See Showroom"
              ads={promoSlot.ads}
            />
          )}

          {/* Below are components not initially visible on the page */}
          {!isPreview && (seller?.traderName ?? seller?.name) && (
            <AdDetailsComponentsThatAreNotInitiallyVisible
              baseUrl={baseUrl}
              cdnUrl={CDN_STATIC_ASSETS}
              adId={id}
              title={sellerTitle}
              sectionName={section?.name}
              illustration={sellerPhoto}
              county={county}
              type={seller.type}
              isTrader={isTrader}
              verification={seller.verification}
              friendlyUrl={friendlyUrl}
              dealer={dealer}
              sellerName={sellerTitle}
              adTitle={header}
              refreshTopAndSideDFPUnit={refreshTopAndSideDFPUnit}
              savedSearchData={
                eligibleForSaveSearch ? savedSearchData : undefined
              }
              relativeUrl={relativeUrl}
              financeInfo={financeInfo}
              reservation={reservation}
              campaignId={campaignId ?? null}
            />
          )}
        </div>
      </DFPSlotsProvider>
    </AdDetailsWrapper>
  );
}

export { AdDetails };
